"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";
import { track } from "@/lib/analytics";
import {
  addLibraryEntry,
  createShareLink,
  type CatalogPiece,
  type LibrarySource,
} from "@/lib/api/api-client";
import { shareLinkDetail } from "@/lib/library/piece-rights";
import { libraryShareUrl } from "@/lib/library/library-routes";
import { useLibraryFeedback } from "./library-feedback";
import { useLibraryOverview } from "./library-overview";

/**
 * Les deux gestes qu'on peut faire sur un morceau qu'on ne possède pas encore,
 * ou qu'on veut faire circuler : l'ajouter, et copier son lien de partage.
 *
 * Le retrait n'est pas ici : il se diffère, il appartient à la liste qui
 * l'affiche, et il a son propre module (`use-deferred-removal.ts`).
 */

export type LibraryActions = {
  /** `true` quand un appel est en vol : les boutons s'y grisent. */
  readonly busy: boolean;
  readonly addToLibrary: (piece: CatalogPiece) => void;
  /**
   * Crée le lien et le met dans le presse-papiers.
   *
   * `source` dit **ce qui circule** — un import, une pièce du catalogue, un
   * morceau qu'on nous a partagé — et non la surface d'où part le clic : c'est
   * la seule question à laquelle la boucle de partage doit répondre. Il est
   * exigé plutôt que deviné : la même carte se partage depuis « Mes morceaux »
   * comme depuis le catalogue, et seul l'appelant a l'entrée sous la main.
   */
  /**
   * `scoreId` quand on sait quel fichier partager — une ligne de « Mes
   * morceaux » ; sinon le serveur prend celui de la personne pour cette œuvre.
   */
  readonly copyShareLink: (piece: CatalogPiece, source: LibrarySource, scoreId?: string) => void;
};

export function useLibraryActions(): LibraryActions {
  const { isAuthenticated } = useAuth();
  const { openAccountModal } = useAccountModal();
  const { notify } = useLibraryFeedback();
  const { refresh } = useLibraryOverview();
  const [busy, setBusy] = useState(false);

  /**
   * Le morceau qu'on voulait ajouter quand l'écran de compte s'est ouvert.
   *
   * On ne grise **jamais** le bouton pour un visiteur anonyme : c'est le
   * meilleur moment d'inscription du produit, parce que c'est le seul où la
   * demande vient de la personne. Elle vient de dire « je veux ce morceau », on
   * répond en lui ouvrant un compte, puis on tient sa demande — sans quoi elle
   * se connecte et retombe sur une carte qui n'a pas bougé.
   */
  const wanted = useRef<CatalogPiece | null>(null);

  const commitAdd = useCallback(
    (piece: CatalogPiece) => {
      setBusy(true);

      addLibraryEntry({ pieceId: piece.id })
        .then((result) => {
          refresh();

          /*
            Le geste d'intention le plus fort du catalogue, et il n'était mesuré
            que **quand il échouait** faute de compte : on comptait les refus,
            jamais les réussites.

            Sur `created` seulement. Un `200` veut dire que la ligne existait
            déjà — ouvrir un morceau la crée —, et le compter ferait passer une
            ouverture d'hier pour un ajout d'aujourd'hui. La source vient de
            l'entrée rendue par le serveur, jamais devinée depuis la surface :
            la même carte s'ajoute depuis le catalogue comme depuis un partage.
          */
          if (result.created) {
            track({
              name: "library_item_added",
              properties: { source: result.item.source, pieceId: piece.id },
            });
          }

          notify({
            /*
              `created` distingue le `201` du `200`, donc on peut écrire
              « Ajouté » plutôt que « Déjà là » sans relire la liste pour le
              deviner. Le second cas arrive plus souvent qu'on ne croit :
              ouvrir un morceau crée déjà sa ligne.
            */
            message: result.created
              ? "Ajouté à mes morceaux."
              : "Tu as déjà ce morceau.",
          });
        })
        .catch(() => {
          notify({
            message: "Ce morceau n'a pas pu être ajouté.",
            failed: true,
          });
        })
        .finally(() => setBusy(false));
    },
    [notify, refresh],
  );

  // La session vient de s'ouvrir : on rejoue la demande mise de côté.
  useEffect(() => {
    if (!isAuthenticated || wanted.current === null) {
      return;
    }

    const piece = wanted.current;
    wanted.current = null;
    commitAdd(piece);
  }, [isAuthenticated, commitAdd]);

  const addToLibrary = useCallback(
    (piece: CatalogPiece) => {
      if (!isAuthenticated) {
        wanted.current = piece;
        openAccountModal("library_add");

        return;
      }

      commitAdd(piece);
    },
    [isAuthenticated, openAccountModal, commitAdd],
  );

  const copyShareLink = useCallback(
    (piece: CatalogPiece, source: LibrarySource, scoreId?: string) => {
      setBusy(true);

      createShareLink(scoreId ? { scoreId } : { pieceId: piece.id })
        .then(async ({ item }) => {
          // L'API ne rend qu'un jeton : elle ne sait pas sous quel nom de
          // domaine le front est servi. L'origine entre donc ici.
          const url = libraryShareUrl(window.location.origin, item.token);
          const copied = await writeToClipboard(url);

          /*
            Le premier palier de la boucle de partage, et il n'avait aucun
            émetteur : on comptait les liens ouverts et les partages acceptés
            sans jamais savoir combien de liens avaient été créés, c'est-à-dire
            sans le dénominateur du seul entonnoir d'acquisition qui ne vienne
            pas de nous.

            Ici et pas au clic : un `403` sur une pièce qu'on ne possède pas ou
            une coupure réseau ne créent aucun lien, et un lien qui n'existe pas
            ne circule pas. Ici et pas dans la branche `copied` non plus : le
            repli affiche le lien en clair, donc il est bel et bien remis à la
            personne — le presse-papiers refusé (Safari hors geste direct) est
            un accident de transport, pas un partage qui n'a pas eu lieu.

            `method: "copy"` en dur : c'est la seule route qui existe. Le produit
            n'ouvre nulle part `navigator.share`, et `native` n'aura d'émetteur
            que le jour où cette feuille de partage existera.

            ⚠️ `item.token` n'entre dans aucune propriété : c'est la capacité qui
            ouvre la bibliothèque de quelqu'un.
          */
          track({
            name: "share_link_created",
            properties: { method: "copy", source },
          });

          notify(
            copied
              ? {
                  message: "Lien copié.",
                  /*
                    Le retour dit la **conséquence**, pas la réussite : que le
                    presse-papiers a pris, la personne le sait déjà. Ce qu'elle
                    ne sait pas, c'est ce que le lien autorise, et sur une œuvre
                    protégée, à quoi ce partage est destiné.
                  */
                  detail: shareLinkDetail(piece),
                }
              : {
                  /*
                    Presse-papiers refusé (Safari hors geste direct, contexte non
                    sécurisé) : on montre le lien plutôt qu'un message d'erreur.
                    Le geste a réussi côté serveur, seule la copie a échoué.
                  */
                  message: "Voici le lien de partage.",
                  detail: url,
                },
          );
        })
        .catch(() => {
          notify({
            message: "Le lien n'a pas pu être créé.",
            failed: true,
          });
        })
        .finally(() => setBusy(false));
    },
    [notify],
  );

  return { busy, addToLibrary, copyShareLink };
}

/**
 * Écrit dans le presse-papiers, `false` si le navigateur refuse.
 *
 * Deux tentatives : l'API moderne, puis la vieille sélection de champ. La
 * seconde reste la seule qui fonctionne sur un Safari sans contexte sécurisé,
 * et c'est exactement le cas d'une préproduction en HTTP.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    // On tente la suite.
  }

  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.remove();
  }
}
